import React__default, { useState, useEffect, createContext, useContext } from 'react';
import { FocusOn } from 'react-focus-on';
import { useId } from '@reach/auto-id';
import { Portal } from '@reach/portal';
import { ModalOverlay } from './ModalOverlay.js';

/* ======================================= */
const ModalContext = /*#__PURE__*/createContext({});
const useModalContext = () => useContext(ModalContext);
/* ======================================= */
/**
 * A Modal is a dialog that appears over the main content and moves the system into a special mode requiring user interaction. Modals are typically used to focus a user's attention for any of these scenarios:
 * - When you want to capture information from the user without having them leave the parent page.
 * - When you want to show additional information to the user without losing context of the parent page.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/modal#usage)
 * - [Props](https://blockparty.endpoint.com/components/modal#props)
 * <br />
 *
 */
const Modal = _ref => {
  let {
    children,
    autoFocus = false,
    id,
    isOpen,
    canCloseOnOverlayClick = true,
    initialFocusRef,
    formatIds = id => ({
      content: `modal-${id}`,
      header: `modal-${id}-header`,
      body: `modal-${id}-body`
    }),
    onClose,
    transitionDuration = 200
  } = _ref;
  const uuid = useId();
  let _id = uuid;
  if (id) {
    _id = id;
  }
  // @ts-ignore
  const contentId = formatIds(_id).content;
  // @ts-ignore
  const headerId = formatIds(_id).header;
  // @ts-ignore
  const bodyId = formatIds(_id).body;
  const context = {
    _id,
    bodyId,
    contentId,
    headerId,
    onClose,
    isOpen,
    initialFocusRef
  };
  const [visible, setVisible] = useState(false);
  const handleOnClose = () => {
    onClose();
    setTimeout(() => {
      setVisible(false);
    }, transitionDuration);
  };
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    }
  }, [isOpen]);
  return /*#__PURE__*/React__default.createElement(ModalContext.Provider, {
    value: context
  }, visible && /*#__PURE__*/React__default.createElement(Portal, null, /*#__PURE__*/React__default.createElement(ModalOverlay, {
    isOpen: isOpen
  }, /*#__PURE__*/React__default.createElement(FocusOn, {
    autoFocus: autoFocus,
    onActivation: () => {
      if (initialFocusRef && initialFocusRef.current) {
        initialFocusRef.current.focus();
      }
    },
    onClickOutside: () => canCloseOnOverlayClick && handleOnClose(),
    onEscapeKey: () => canCloseOnOverlayClick && onClose()
  }, children))));
};

export { Modal, useModalContext };
